import React, {
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";
import styled from "styled-components";

import AdminSearchInput
  from "./AdminSearchInput";


/**
 * AdminLookup
 *
 * Generic async lookup presentation authority for Q360 Admin.
 *
 * Responsibilities:
 * - receive user search input;
 * - debounce lookup requests;
 * - expose loading / empty / error states;
 * - render generic result options;
 * - provide accessible combobox / listbox semantics;
 * - provide keyboard navigation;
 * - notify the consumer when an option is selected.
 *
 * Explicitly excluded:
 * - domain-specific knowledge;
 * - business lookup rules;
 * - persistence;
 * - business-object mutation;
 * - Admin scope resolution;
 * - backend authority;
 * - Runtime authority.
 */
export default function AdminLookup({
  value = "",
  onInputChange = null,
  onSelect,
  search,
  getOptionKey = defaultGetOptionKey,
  getOptionLabel = defaultGetOptionLabel,
  renderOption = null,
  placeholder = "Rechercher...",
  minChars = 2,
  debounceMs = 300,
  loadingLabel = "Recherche...",
  emptyLabel = "Aucun résultat.",
  errorLabel =
    "Impossible d'effectuer la recherche.",
  disabled = false,
  ariaLabel = "Recherche",
}) {
  const generatedId =
    useId();

  const listboxId =
    `admin-lookup-${generatedId}-listbox`;

  const requestSequenceRef =
    useRef(0);

  const [
    query,
    setQuery,
  ] = useState(
    value ?? ""
  );

  const [
    options,
    setOptions,
  ] = useState([]);

  const [
    open,
    setOpen,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(null);

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(-1);

  /*
   * Search intent is deliberately distinct from the controlled value.
   *
   * A consumer may provide an existing selected value when a form is
   * loaded. Synchronizing that value is presentation state only and
   * must not manufacture a lookup request or open the result popup.
   *
   * Lookup execution begins only after explicit user input.
   */
  const [
    searchIntent,
    setSearchIntent,
  ] = useState(false);


  useEffect(
    () => {
      setQuery(
        value ?? ""
      );

      /*
       * Controlled-value synchronization is not a user search.
       *
       * This is especially important for edit forms where the
       * authoritative value already exists when AdminLookup mounts.
       */
      setSearchIntent(false);

      requestSequenceRef.current += 1;

      setOptions([]);
      setOpen(false);
      setLoading(false);
      setError(null);
      setActiveIndex(-1);
    },
    [
      value,
    ]
  );


  useEffect(
    () => {
      if (
        disabled
        || !searchIntent
      ) {
        requestSequenceRef.current += 1;

        setOptions([]);
        setOpen(false);
        setLoading(false);
        setError(null);
        setActiveIndex(-1);

        return undefined;
      }

      const normalizedQuery =
        String(
          query ?? ""
        ).trim();

      if (
        normalizedQuery.length
        < minChars
      ) {
        requestSequenceRef.current += 1;

        setOptions([]);
        setOpen(false);
        setLoading(false);
        setError(null);
        setActiveIndex(-1);

        return undefined;
      }

      const requestSequence =
        requestSequenceRef.current
        + 1;

      requestSequenceRef.current =
        requestSequence;

      const timer =
        window.setTimeout(
          async () => {
            setLoading(true);
            setError(null);
            setOpen(true);
            setActiveIndex(-1);

            try {
              const result =
                await search(
                  normalizedQuery
                );

              if (
                requestSequence
                !== requestSequenceRef.current
              ) {
                return;
              }

              const nextOptions =
                Array.isArray(result)
                  ? result
                  : [];

              setOptions(
                nextOptions
              );

              setLoading(false);
              setOpen(true);
              setActiveIndex(
                nextOptions.length > 0
                  ? 0
                  : -1
              );
            } catch (caught) {
              if (
                requestSequence
                !== requestSequenceRef.current
              ) {
                return;
              }

              setOptions([]);
              setLoading(false);
              setOpen(true);
              setActiveIndex(-1);

              setError(
                caught instanceof Error
                  ? caught
                  : new Error(
                      String(caught)
                    )
              );
            }
          },
          debounceMs
        );

      return () => {
        window.clearTimeout(
          timer
        );
      };
    },
    [
      query,
      search,
      minChars,
      debounceMs,
      disabled,
      searchIntent,
    ]
  );


  const changeQuery = (
    event
  ) => {
    const nextQuery =
      event.target.value;

    setQuery(
      nextQuery
    );

    setSearchIntent(true);
    setActiveIndex(-1);

    if (
      typeof onInputChange
        === "function"
    ) {
      onInputChange(
        nextQuery
      );
    }
  };


  const selectOption = (
    option
  ) => {
    const label =
      getOptionLabel(
        option
      );

    setQuery(
      label ?? ""
    );

    setSearchIntent(false);
    setOpen(false);
    setActiveIndex(-1);

    onSelect(
      option
    );
  };


  const moveActive = (
    direction
  ) => {
    if (
      !open
      || options.length === 0
    ) {
      return;
    }

    setActiveIndex(
      (current) => {
        if (current < 0) {
          return direction > 0
            ? 0
            : options.length - 1;
        }

        return (
          current
          + direction
          + options.length
        ) % options.length;
      }
    );
  };


  const handleKeyDown = (
    event
  ) => {
    if (
      event.key === "ArrowDown"
    ) {
      event.preventDefault();

      if (!open) {
        setOpen(true);
      }

      moveActive(1);
      return;
    }

    if (
      event.key === "ArrowUp"
    ) {
      event.preventDefault();

      if (!open) {
        setOpen(true);
      }

      moveActive(-1);
      return;
    }

    if (
      event.key === "Enter"
      && open
      && activeIndex >= 0
      && options[
        activeIndex
      ]
    ) {
      event.preventDefault();

      selectOption(
        options[
          activeIndex
        ]
      );

      return;
    }

    if (
      event.key === "Escape"
    ) {
      event.preventDefault();

      setOpen(false);
      setActiveIndex(-1);
    }
  };


  const activeOptionId =
    open
    && activeIndex >= 0
    && options[
      activeIndex
    ]
      ? createOptionId(
          listboxId,
          getOptionKey(
            options[
              activeIndex
            ]
          )
        )
      : undefined;


  const shouldRenderPopup =
    open
    && (
      loading
      || error
      || options.length > 0
      || String(query ?? "")
        .trim()
        .length >= minChars
    );


  return (
    <LookupRoot
      data-admin-lookup
    >
      <AdminSearchInput
        role="combobox"
        aria-label={
          ariaLabel
        }
        aria-autocomplete="list"
        aria-expanded={
          open
        }
        aria-controls={
          listboxId
        }
        aria-activedescendant={
          activeOptionId
        }
        autoComplete="off"
        value={
          query
        }
        placeholder={
          placeholder
        }
        disabled={
          disabled
        }
        onChange={
          changeQuery
        }
        onFocus={() => {
          /*
           * Focus is an explicit user interaction.
           *
           * Unlike controlled-value synchronization,
           * it may establish lookup intent. The normal
           * minChars contract remains authoritative:
           *
           * - minChars = 0 -> search("") may expose
           *   all available options;
           * - minChars > 0 -> no lookup occurs until
           *   enough characters are entered.
           */
          setSearchIntent(true);

          if (
            options.length > 0
            || loading
            || error
          ) {
            setOpen(true);
          }
        }}
        onKeyDown={
          handleKeyDown
        }
      />

      {shouldRenderPopup ? (
        <LookupPopup
          id={
            listboxId
          }
          role="listbox"
          aria-label={`${ariaLabel} — résultats`}
        >
          {loading ? (
            <LookupState
              data-admin-lookup-loading
            >
              {loadingLabel}
            </LookupState>
          ) : error ? (
            <LookupState
              role="alert"
              data-admin-lookup-error
            >
              {errorLabel}
            </LookupState>
          ) : options.length === 0 ? (
            <LookupState
              data-admin-lookup-empty
            >
              {emptyLabel}
            </LookupState>
          ) : (
            options.map(
              (
                option,
                index
              ) => {
                const optionKey =
                  getOptionKey(
                    option
                  );

                const optionLabel =
                  getOptionLabel(
                    option
                  );

                const selected =
                  index
                  === activeIndex;

                return (
                  <LookupOption
                    key={
                      String(
                        optionKey
                      )
                    }
                    id={
                      createOptionId(
                        listboxId,
                        optionKey
                      )
                    }
                    role="option"
                    aria-selected={
                      selected
                    }
                    data-active={
                      selected
                        ? "true"
                        : "false"
                    }
                    onMouseEnter={() =>
                      setActiveIndex(
                        index
                      )
                    }
                    onMouseDown={(
                      event
                    ) => {
                      /*
                       * Prevent the input blur
                       * from closing the popup
                       * before selection.
                       */
                      event.preventDefault();

                      selectOption(
                        option
                      );
                    }}
                  >
                    {typeof renderOption
                      === "function"
                      ? renderOption(
                          option,
                          {
                            active:
                              selected,
                            index,
                          }
                        )
                      : optionLabel}
                  </LookupOption>
                );
              }
            )
          )}
        </LookupPopup>
      ) : null}
    </LookupRoot>
  );
}


function defaultGetOptionKey(
  option
) {
  if (
    option
    && typeof option
      === "object"
  ) {
    return (
      option.id
      ?? option.uuid
      ?? option.key
      ?? defaultGetOptionLabel(
        option
      )
    );
  }

  return String(
    option ?? ""
  );
}


function defaultGetOptionLabel(
  option
) {
  if (
    option
    && typeof option
      === "object"
  ) {
    return String(
      option.label
      ?? option.name
      ?? option.title
      ?? option.id
      ?? option.uuid
      ?? ""
    );
  }

  return String(
    option ?? ""
  );
}


function createOptionId(
  listboxId,
  key
) {
  const safeKey =
    String(
      key ?? "option"
    )
      .replace(
        /[^a-zA-Z0-9_-]+/g,
        "-"
      );

  return (
    `${listboxId}-option-${safeKey}`
  );
}


AdminLookup.propTypes = {
  value:
    PropTypes.string,

  onInputChange:
    PropTypes.func,

  onSelect:
    PropTypes.func.isRequired,

  search:
    PropTypes.func.isRequired,

  getOptionKey:
    PropTypes.func,

  getOptionLabel:
    PropTypes.func,

  renderOption:
    PropTypes.func,

  placeholder:
    PropTypes.string,

  minChars:
    PropTypes.number,

  debounceMs:
    PropTypes.number,

  loadingLabel:
    PropTypes.node,

  emptyLabel:
    PropTypes.node,

  errorLabel:
    PropTypes.node,

  disabled:
    PropTypes.bool,

  ariaLabel:
    PropTypes.string,
};


const LookupRoot =
  styled.div`
    position: relative;

    display: grid;
    gap: 0.4rem;

    width: 100%;
    min-width: 0;

    > input {
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
  `;


const LookupPopup =
  styled.div`
    position: absolute;
    z-index: 60;

    top:
      calc(
        100%
        + 0.35rem
      );

    left: 0;
    right: 0;

    max-height: 320px;
    overflow-y: auto;

    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.13
      );

    border-radius: 12px;

    background:
      #0b1329;

    box-shadow:
      0
      18px
      40px
      rgba(
        0,
        0,
        0,
        0.28
      );
  `;


const LookupOption =
  styled.div`
    padding:
      0.75rem
      0.85rem;

    cursor: pointer;

    color:
      #f7f7f7;

    line-height: 1.35;

    &[data-active="true"] {
      background:
        rgba(
          137,
          179,
          255,
          0.15
        );
    }

    &:hover {
      background:
        rgba(
          137,
          179,
          255,
          0.12
        );
    }
  `;


const LookupState =
  styled.div`
    padding:
      0.8rem
      0.85rem;

    color:
      rgba(
        247,
        247,
        247,
        0.68
      );

    line-height: 1.4;
  `;
